import { Group } from "@fairfleet/geotab";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import Popover from "@mui/material/Popover";
import { MouseEvent, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { ToolbarIconButton } from "../common/ToolbarIconButton";
import { GroupMultiSelect } from "./GroupMultiSelect";

interface Props {
  value: Group[];
  onChange: (groups: Group[]) => void;
}

/** The group filter as a toolbar icon; it is rarely needed, so the field opens in a popover. */
export function GroupFilterButton({ value, onChange }: Props) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const popoverId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const label = value.length
    ? t("logbookAudit.filters.groupsActive", { count: value.length })
    : t("logbookAudit.filters.groups");

  return (
    <>
      <ToolbarIconButton
        label={label}
        icon={<AccountTreeOutlinedIcon fontSize="small" aria-hidden />}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        aria-controls={anchor ? popoverId : undefined}
        badge={value.length}
      />
      <Popover
        id={popoverId}
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // The popover renders in a portal outside the page's theme box, so the font is set here.
        slotProps={{
          transition: { onEntered: () => inputRef.current?.focus() },
          paper: {
            sx: { width: 360, maxWidth: "calc(100vw - 32px)", p: 1.5, borderRadius: `${AUDIT_TOKENS.radius.card}px`, boxShadow: AUDIT_TOKENS.shadow.panel, border: `1px solid ${AUDIT_TOKENS.line}`, fontFamily: AUDIT_TOKENS.fontFamily },
            role: "dialog",
            "aria-label": t("logbookAudit.filters.groups"),
          },
        }}
      >
        <GroupMultiSelect value={value} onChange={onChange} inputRef={inputRef} />
      </Popover>
    </>
  );
}
